/**
 * Multimodal Attach → Production Requirement APIs.
 * Upload to S3 via presigned PUT, register attachment in DB, poll analysis.
 */
import api from '@/lib/api';
import {
  INTAKE_ATTACHMENT_ACCEPT,
  intakeFileKind,
  isAllowedIntakeFile,
} from '@/lib/intakeFileUpload';

export { INTAKE_ATTACHMENT_ACCEPT, intakeFileKind, isAllowedIntakeFile };

export const MAX_PRODUCTION_ATTACHMENTS = 12;

export const ATTACH_ACCEPT = {
  audio: 'audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac',
  video: 'video/*,.mp4,.mov,.webm,.mkv,.m4v',
  image: 'image/*,.png,.jpg,.jpeg,.webp,.gif',
  file: '.pdf,.doc,.docx,.ppt,.pptx,.txt,.md,.rtf,.csv,.xls,.xlsx,application/pdf,text/*',
};

const EXT_MIME = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  webm: 'video/webm',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  mkv: 'video/x-matroska',
  m4v: 'video/x-m4v',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  gif: 'image/gif',
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function formatBytes(n) {
  const bytes = Number(n) || 0;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export { styleNoteFromAnalysis, isStylePlaceholderNote } from '@/lib/visualStyleNotes';

export function formatDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function guessContentType(file, filename) {
  const fromFile = (file?.type || '').trim();
  if (fromFile && fromFile !== 'application/octet-stream') return fromFile;
  const ext = String(filename || file?.name || '')
    .split('.')
    .pop()
    ?.toLowerCase();
  return EXT_MIME[ext] || 'application/octet-stream';
}

function normalizeAttachmentTypeHint(hint, file, filename, contentType) {
  const h = String(hint || '').toLowerCase();
  if (h === 'recording') return 'recording';
  if (['audio', 'video', 'image', 'document', 'file'].includes(h)) {
    return h === 'file' ? 'document' : h;
  }
  return intakeFileKind(contentType, filename || file?.name) || 'document';
}

/**
 * 1) POST /media/presigned-url
 * 2) PUT file to S3
 * 3) POST /production/attachments (persist URL + metadata in DB)
 */
export async function uploadProductionAttachment(file, opts = {}) {
  if (!file) throw new Error('No file selected');
  const filename =
    opts.filename ||
    file.name ||
    (opts.source === 'record' ? `voice-instruction-${Date.now()}.webm` : `upload-${Date.now()}`);
  const contentType = guessContentType(file, filename);
  const attachmentType = normalizeAttachmentTypeHint(
    opts.attachmentType,
    file,
    filename,
    contentType
  );

  if (opts.source !== 'record' && file instanceof File && !isAllowedIntakeFile(file)) {
    // Allow when extension is known even if browser MIME is empty
    const ext = filename.split('.').pop()?.toLowerCase();
    if (!EXT_MIME[ext]) {
      throw new Error(`Unsupported file: ${filename}`);
    }
  }

  let presigned;
  try {
    const res = await api.post('/media/presigned-url', null, {
      params: { filename, content_type: contentType },
    });
    presigned = res.data;
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'Presign failed';
    throw new Error(`Could not get S3 upload URL: ${detail}`);
  }

  const uploadUrl = presigned.upload_url || presigned.presigned_url;
  const s3Key = presigned.s3_key;
  const viewUrl = presigned.view_url || presigned.object_url || presigned.s3_url || '';
  if (!uploadUrl || !s3Key) throw new Error('Could not get upload URL');

  let putRes;
  try {
    putRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType },
    });
  } catch (err) {
    throw new Error(`S3 upload network error: ${err?.message || 'failed'}`);
  }
  if (!putRes.ok) {
    const body = await putRes.text().catch(() => '');
    throw new Error(`S3 upload failed (${putRes.status})${body ? `: ${body.slice(0, 120)}` : ''}`);
  }

  const sizeBytes = file.size || 0;
  try {
    const { data } = await api.post('/production/attachments', {
      s3_key: s3Key,
      s3_url: viewUrl,
      filename,
      content_type: contentType,
      size_bytes: sizeBytes,
      attachment_type: attachmentType === 'file' ? 'document' : attachmentType,
      project_id: opts.projectId || null,
      source: opts.source === 'record' ? 'record' : 'attach',
      process_async: opts.processAsync !== false,
    });
    if (!data?.attachment?.id) {
      throw new Error('Attachment was uploaded but not saved in the database');
    }
    return {
      attachment: data.attachment,
      projectId: data.project_id,
    };
  } catch (err) {
    if (err?.message?.includes('not saved')) throw err;
    const detail = err?.response?.data?.detail || err?.message || 'DB save failed';
    throw new Error(`Uploaded to S3 but failed to save attachment: ${detail}`);
  }
}

export async function fetchAttachment(attachmentId) {
  const { data } = await api.get(`/production/attachments/${attachmentId}`);
  return data.attachment;
}

export async function retryAttachment(attachmentId) {
  const { data } = await api.post(`/production/attachments/${attachmentId}/retry`);
  return data;
}

export async function deleteProductionAttachment(attachmentId) {
  const { data } = await api.delete(`/production/attachments/${attachmentId}`);
  return data;
}

export async function listProjectAttachments(projectId) {
  const { data } = await api.get(`/production/projects/${projectId}/attachments`);
  return data.attachments || [];
}

export async function synthesizeRequirements(payload) {
  const { data } = await api.post('/production/requirements/synthesize', payload);
  return data;
}

export async function completeRequirements(projectId, answers, confirm = false) {
  const { data } = await api.post('/production/requirements/complete', {
    project_id: projectId,
    answers,
    confirm,
  });
  return data;
}

export async function approveRequirements(projectId, edits = {}) {
  const { data } = await api.post('/production/requirements/approve', {
    project_id: projectId,
    edits,
  });
  return data;
}

export async function getRequirement(projectId) {
  const { data } = await api.get(`/production/projects/${projectId}/requirement`);
  return data;
}

/** Poll until analyzed/failed or timeout. */
export async function pollAttachmentUntilDone(attachmentId, { intervalMs = 1500, timeoutMs = 120000 } = {}) {
  const start = Date.now();
  let last = null;
  while (Date.now() - start < timeoutMs) {
    try {
      last = await Promise.race([
        fetchAttachment(attachmentId),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error('attachment poll hung')), Math.min(8000, intervalMs + 6000));
        }),
      ]);
    } catch (err) {
      if (String(err?.message || '') !== 'attachment poll hung') throw err;
    }
    const st = last?.processing_status;
    if (st === 'analyzed' || st === 'failed') return last;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return last;
}
